/**
 * Pure helper for building the Firestore document body of a new pet
 * `MemberProfile`. Mirrors the iOS save path in `AddPetView.swift:76-118`
 * (`savePet()`).
 *
 * The function is DOM-free and Firestore-free — it returns only the field
 * deltas that the caller passes to `setDoc(...)`. This keeps it
 * unit-testable without mocking either side, matching the pattern used in
 * `createResponsibility.ts`.
 *
 * Field-shape rules:
 * - `memberType` is always `"pet"`.
 * - `name` and `nickname` both receive the trimmed `name` input — iOS sets
 *   them in lockstep so that downstream readers (which sometimes prefer
 *   `nickname` over `name`) see the same string either way.
 * - Species + breed are stored under `details` to match
 *   `MemberProfile.species` / `.breed` accessors on iOS
 *   (Household.swift:340-341). `breed` is dropped when blank.
 * - Birthday is decomposed into `birthMonth` (1-12) + `birthDay` (1-31) so
 *   it can be rendered without a privacy-sensitive year. Matches iOS
 *   `birthMonth`/`birthDay` (Household.swift:283-284).
 * - The household ID is recorded both as a single-element `households`
 *   array (so the pet doc itself can be queried by household) AND
 *   separately added to `households/{hh}.household_members` by the caller
 *   via `arrayUnion`.
 */

import { Timestamp } from "firebase/firestore";
import type { MemberProfile } from "./models";

/**
 * Allowed species values for the Add Pet form. Mirrors the iOS picker in
 * `AddPetView.swift:20` exactly: `[dog, cat, bird, rabbit, fish, reptile,
 * other]`. The Android client uses the same ordered set.
 */
export type PetSpecies =
  | "dog"
  | "cat"
  | "bird"
  | "rabbit"
  | "fish"
  | "reptile"
  | "other";

export const PET_SPECIES_OPTIONS: PetSpecies[] = [
  "dog",
  "cat",
  "bird",
  "rabbit",
  "fish",
  "reptile",
  "other",
];

export interface PetFormInput {
  /** Required, trimmed before validation. */
  name: string;
  species: PetSpecies;
  /** Optional. Empty/whitespace strings are dropped. */
  breed?: string;
  /**
   * Optional birthday — ISO date string from `<input type="date">`
   * ("YYYY-MM-DD"). Retained for legacy callers; modern callers should
   * pass `birthMonth` / `birthDay` / `birthYear` directly to mirror the
   * iOS month/day/(optional)year split (`AddPetView.swift:42-54`).
   *
   * When the explicit numeric fields are present and valid, they take
   * precedence over `birthday`.
   */
  birthday?: string;
  /** 1-12. Required when `birthDay` is set. */
  birthMonth?: number | null;
  /** 1-31. Required when `birthMonth` is set. */
  birthDay?: number | null;
  /** Optional 4-digit year. iOS keeps this optional for privacy. */
  birthYear?: number | null;
  /** Optional Storage download URL for the pet's avatar. */
  profileUrl?: string;
  /** Optional notes / additional info. */
  notes?: string;
}

/**
 * Build the Firestore document body for a new pet, or return `null` if
 * required fields are missing / invalid. Required: `name`, `householdId`,
 * `petUid`.
 *
 * The returned object intentionally INCLUDES `uid` so the caller can use
 * `setDoc(doc(db, "member_profiles", petUid), body)` (rather than
 * `addDoc`, which would generate a different ID than the one we already
 * pushed to `household_members`).
 */
export function buildPetProfileDoc(
  form: PetFormInput,
  householdId: string,
  petUid: string,
  createdBy?: string,
): MemberProfile | null {
  const name = form.name.trim();
  if (!name) return null;
  if (!householdId) return null;
  if (!petUid) return null;

  const details: Record<string, string> = {
    species: form.species,
  };
  const breed = form.breed?.trim();
  if (breed) details.breed = breed;

  const doc: MemberProfile = {
    uid: petUid,
    memberType: "pet",
    name,
    nickname: name,
    isActive: true,
    households: [householdId],
    details,
    createdAt: Timestamp.now(),
  };

  if (createdBy) {
    doc.createdBy = createdBy;
  }

  // Prefer the explicit month/day/year inputs (iOS-parity path). Fall
  // back to parsing the legacy `birthday` ISO string only when the
  // explicit fields are absent — this lets older callers keep working
  // while the new modal writes structured fields directly.
  const explicit = parseExplicitBirthday(
    form.birthMonth,
    form.birthDay,
    form.birthYear,
  );
  const birthday = explicit ?? parseBirthday(form.birthday);
  if (birthday) {
    doc.birthMonth = birthday.month;
    doc.birthDay = birthday.day;
    if (birthday.year !== undefined) {
      doc.birthYear = birthday.year;
    }
  }

  const profileUrl = form.profileUrl?.trim();
  if (profileUrl) {
    doc.profile_url = profileUrl;
  }

  const notes = form.notes?.trim();
  if (notes) {
    doc.notes = notes;
  }

  return doc;
}

/**
 * Parse an ISO date string ("YYYY-MM-DD") from `<input type="date">` into
 * 1-indexed `{ month, day, year }`. Returns `null` if the input is
 * blank, malformed, or out of range.
 *
 * We intentionally avoid `new Date(input)` because it interprets bare ISO
 * dates as UTC, which can shift the day for users east of UTC (e.g.
 * "2024-03-15" becomes "2024-03-14" in negative timezones).
 */
export function parseBirthday(
  birthday: string | undefined,
): { month: number; day: number; year?: number } | null {
  if (!birthday) return null;
  const trimmed = birthday.trim();
  if (!trimmed) return null;

  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (!match) return null;

  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const day = parseInt(match[3], 10);

  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return null;
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;

  // The HTML date input always emits a year, but the iOS form keeps
  // birthYear optional. Treat 1900 as a sentinel for "no year" — that
  // matches what the existing iOS reader does (it just renders a
  // year-less date when birthYear is missing). For now we simply pass
  // the year through; callers can elect not to display it.
  return { month, day, year };
}

/**
 * Validate the explicit month/day/year birthday inputs from the iOS-style
 * three-field form. Returns `null` when month or day is missing (year is
 * always optional) or out of range. The shape matches `parseBirthday`'s
 * return so callers can use either source interchangeably.
 */
export function parseExplicitBirthday(
  month: number | null | undefined,
  day: number | null | undefined,
  year: number | null | undefined,
): { month: number; day: number; year?: number } | null {
  // Both month and day are required for a valid birthday — iOS hides the
  // birthday entirely if either is missing.
  if (month == null || day == null) return null;
  if (!Number.isFinite(month) || !Number.isFinite(day)) return null;
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;

  if (year != null && Number.isFinite(year) && year > 0) {
    return { month, day, year };
  }
  return { month, day };
}
